import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { ACCESS_TOKEN, API_RESPONSE, SIGNIN_AUTH_TOKEN } from "../../settings/shared/constants";
import { jwtDecode } from 'jwt-decode';
import LoginService from "../../services/api/login";
import UserService from "../../services/api/user";

import { showNotificationGlobal } from "../../components/common/NotificationProvider";
import { ClipLoader } from "react-spinners";
import {
  completeDesktopOAuth,
  isDesktopOAuth,
  preserveDesktopOAuthAcrossClear,
} from "../../settings/shared/desktopOAuth";

const OAuth2RedirectHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);


  const getQueryParam = (param) => {
    const params = new URLSearchParams(location.search);
    return params.get(param) || "";
  };

  const moveLogin = () => {
    navigate("/auth/login");
  }

  const getUserDetails = () => {
    UserService.getuserdetails()
      .then((res) => {
        localStorage.setItem("profile", JSON.stringify(res));
        localStorage.setItem("accountId", res.accountId);
        localStorage.setItem("tenant", res.tenant);
        showNotificationGlobal("Logged in successfully.", "success");
        setTimeout(() => {
          navigate("/apps");
        }, 1000);
      }).catch((error) => {
        setIsLoading(false);
        moveLogin();
      });
  };

  const finishDesktop = async (accessToken) => {
    showNotificationGlobal("Opening SuperAgentX Desktop…", "success");
    await completeDesktopOAuth({
      accessToken,
      authorizeFn: (payload) => LoginService.authorizeDesktop(payload),
    });
  };

  const handleOAuth2Callback = async (code, state) => {
    const provider = sessionStorage.getItem("oauthProvider") || "google";
    sessionStorage.removeItem("oauthProvider");
    const postData = {
      code: code,
      state: state,
      provider: provider
    }
    const response = await LoginService.oauth2Callback(postData);
    if (response.status !== API_RESPONSE.SUCCESS) {
      setIsLoading(false);
      moveLogin();
      return showNotificationGlobal(response.errorMessage || "Authentication failed", "error");
    }

    var decodedEmail = jwtDecode(response?.authToken)

    if (!decodedEmail?.sub) {
      setIsLoading(false);
      moveLogin();
      return showNotificationGlobal("Invalid email", "error");
    }

    // Store OTP valid session token
    sessionStorage.setItem(SIGNIN_AUTH_TOKEN, response?.authToken);

    const tenants = response?.accountsDTOS;
    if (!tenants || tenants.length === 0) {
      setIsLoading(false);
      moveLogin();
      return showNotificationGlobal(response.errorMessage || "No Account found", "error");
    }

    // Persist tenant list for in-app tenant switching later
    try {
      localStorage.setItem("tenantList", JSON.stringify(tenants));
      localStorage.setItem("loginEmail", decodedEmail?.sub);
    } catch (e) {}

    // Multiple tenant case → Navigate to tenant selection
    if (tenants.length > 1) {
      setIsLoading(false);
      return navigate("/auth/tenants", {
        state: { tenantList: tenants, email: decodedEmail?.sub }
      });
    }

    // Single tenant
    const tenant = tenants[0];
    localStorage.setItem("accountLogo", tenant?.accountLogo);
    localStorage.setItem("accountName", tenant?.accountName);

    const tenantData = {
      email: decodedEmail?.sub,
      authToken: sessionStorage.getItem(SIGNIN_AUTH_TOKEN),
      accountId: tenant?.accountID,
    };

    const validateResponse = await LoginService.validateAuth(tenantData);

    if (validateResponse.status !== API_RESPONSE.SUCCESS) {
      setIsLoading(false);
      moveLogin();
      return showNotificationGlobal(validateResponse.errorMessage || "Authentication failed", "error");
    }

    // Final login token + decode
    const decoded = jwtDecode(validateResponse.token);
    localStorage.setItem(ACCESS_TOKEN, validateResponse.token);
    localStorage.setItem("Uid", decoded?.id);
    if (decoded?.roles?.length > 0) {
      localStorage.setItem("role", decoded.roles[0]);
    }

    if (isDesktopOAuth()) {
      setIsLoading(false);
      try {
        await finishDesktop(validateResponse.token);
      } catch (err) {
        showNotificationGlobal(
          err?.message || "Could not return to SuperAgentX Desktop. Try again.",
          "error"
        );
        moveLogin();
      }
      return;
    }

    const restoreDesktop = preserveDesktopOAuthAcrossClear();
    sessionStorage.clear(); // OTP auth no longer needed
    restoreDesktop();
    setIsLoading(false);

    return getUserDetails();
  };

  useEffect(() => {
    const code = getQueryParam("code");
    const state = getQueryParam("state");
    handleOAuth2Callback(code, state);
  }, []);

  return (
    <div>
      {isLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
          <ClipLoader size={50} />
        </div>
      ) : null}
    </div>
  );
};

export default OAuth2RedirectHandler;
